import { NavLink, Outlet } from 'react-router-dom'
import { useAuth } from '@/lib/auth'
import { ROLE_LABELS, type ConfigCheck, type SystemStatus } from '@/lib/api'
import { useFetch } from '@/lib/hooks'

/**
 * The navigation is grouped by what an operator is trying to do, not by which
 * table backs the page.
 *
 * "What gets served" is the day-to-day work — worklists, the rules that select
 * them, and the simulator that says what a device would actually receive.
 * "Who receives it" is the access-control surface and sits below, because
 * changing a device or a facility decides who sees whose patients and is a
 * bigger deal than editing a list.
 */
const NAV_SERVING = [
  { to: '/', label: 'Overview', end: true },
  { to: '/worklists', label: 'Worklists' },
  { to: '/rules', label: 'Rules' },
  { to: '/simulator', label: 'Rule simulator' },
  { to: '/orders', label: 'Scheduled steps' },
]

const NAV_ACCESS = [
  { to: '/devices', label: 'Devices' },
  { to: '/facilities', label: 'Facilities' },
  { to: '/listeners', label: 'Listeners' },
  { to: '/sources', label: 'Sources' },
]

const NAV_OPS = [{ to: '/query-log', label: 'Query log' }]

const SECURITY_NAV = [{ to: '/users', label: 'Users' }]

export function Layout() {
  const { user, signOut, can } = useAuth()
  const { data: status, error } = useFetch<SystemStatus>('/api/v1/system/status', 15000)
  // Polled alongside the status so a configuration problem introduced on one
  // page shows up in the header while somebody is still on another.
  const { data: checks } = useFetch<ConfigCheck[]>('/api/v1/system/checks', 60000)

  const errors = (checks ?? []).filter((c) => c.level === 'error').length
  const warnings = (checks ?? []).filter((c) => c.level === 'warning').length

  const store: Pill = error
    ? { value: 'UNKNOWN', tone: 'idle' }
    : status?.store.reachable
      ? { value: 'REACHABLE', tone: 'ok' }
      : status
        ? { value: 'UNREACHABLE', tone: 'error' }
        : { value: '—', tone: 'idle' }

  const config: Pill =
    checks == null
      ? { value: '—', tone: 'idle' }
      : errors > 0
        ? { value: `${errors} PROBLEM${errors === 1 ? '' : 'S'}`, tone: 'error' }
        : warnings > 0
          ? { value: `${warnings} WARNING${warnings === 1 ? '' : 'S'}`, tone: 'warn' }
          : { value: 'OK', tone: 'ok' }

  return (
    <div className="app">
      <aside className="sidebar">
        <div className="brand">
          <span className="brand-mark">MWL</span>
          <span className="brand-sub">Worklist Console</span>
        </div>

        <nav>
          <span className="nav-section">What gets served</span>
          {NAV_SERVING.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')}
            >
              {item.label}
            </NavLink>
          ))}

          <span className="nav-section">Who receives it</span>
          {NAV_ACCESS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')}
            >
              {item.label}
            </NavLink>
          ))}

          <span className="nav-section">Operations</span>
          {NAV_OPS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')}
            >
              {item.label}
            </NavLink>
          ))}

          {can('security_admin') &&
            SECURITY_NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')}
              >
                {item.label}
              </NavLink>
            ))}
        </nav>

        <div className="sidebar-foot">
          <div className="signed-in">
            <span className="label">Signed in</span>
            <span className="value">{user?.display_name || user?.username}</span>
            <span className="stat-hint">{user ? ROLE_LABELS[user.role] : ''}</span>
            <button className="btn btn-small" onClick={() => void signOut()}>
              Sign out
            </button>
          </div>
          <div className="ae-badge">
            <span className="label">MwlConfig</span>
            <span className="value mono">{status?.store.target ?? '—'}</span>
          </div>
        </div>
      </aside>

      <div className="main">
        <header className="topbar">
          <div className="status-group">
            <StatusPill label="Database" pill={store} />
            <StatusPill label="Configuration" pill={config} />
          </div>
          {status && !status.daemon_binary_present && (
            <div className="topbar-error">
              daemon binary missing — the rule simulator cannot answer
            </div>
          )}
          {error && <div className="topbar-error">status unavailable</div>}
        </header>

        <main className="content">
          <Outlet />
        </main>
      </div>
    </div>
  )
}

interface Pill {
  value: string
  tone: 'ok' | 'warn' | 'error' | 'idle'
}

function StatusPill({ label, pill }: { label: string; pill: Pill }) {
  return (
    <span className={`pill pill-${pill.tone}`}>
      <span className="pill-label">{label}</span>
      <span className="pill-state">{pill.value}</span>
    </span>
  )
}
